/**
 * 이상한 방 - 변화 동사 검증 레이어
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4.2 / 4.3 / 5절
 *
 * 이 파일은 원칙 2("물리적으로 이상한 건 허용, 시각적으로 찾을 수 없는 건 금지")의 **집행자**다.
 * 4.3절이 못박은 대로 원칙만 있고 집행자가 없으면 원칙이 아니다.
 *
 * 집행 항목:
 *  - 무변화 회전 차단 (`rotSymmetry`, 4.2절) - 원형 러그를 90° 돌리는 것은 난이도가 아니라 사기다
 *  - 같은 색으로의 색 변경 차단 / 같은 셀로의 이동 차단 (4.3절 표)
 *  - 셀 점유 규칙 (3.1절) 과 방 경계 (3.1절: 그리드가 유한하므로 방 밖 배출이 자동 차단됨)
 *  - 허용 동사 게이트 (5.3절: 동사 조합은 플레이테스트 A/B 대상이라 설정값이다)
 *  - 한 오브젝트 = 최대 한 변화 (5.1절) - 체인저 페이즈에서만 켠다
 *  - 옮김은 높이(y)를 바꾸지 않는다 - 체인저 페이즈에서만 켠다. 벽의 거울이 바닥에 눕는 것은 변화가 아니라 렌더 사고다.
 *    고정된 것은 **높이 하나**다. 벽 물건이 벽면을 떠나 허공에 뜨는 옮김은 아직 막지 않는다(생성기도 같은 변화를 낸다 - 미해결, 로드맵 7절 P1)
 *  - 손댈 수 있는 사각형(`allowedRect`) - 여럿이 판에서 각자 자기 구획만 고친다(로드맵 M11). 여기 있는 이유가 규칙이다:
 *    "남의 구획이라 안 된다"를 리듀서 게이트로 적으면 도구(`input/changerTool`)가 같은 비교식을 한 벌 더 들어야 하고,
 *    그러면 잠긴 팔레트와 호스트 판정이 갈리는 날이 온다. 문맥으로 내리면 `canPlace`·`resolveTarget`이 답하므로 사본이 없다.
 *
 * 거부는 오류 메시지가 아니라 **UI에서 아예 선택 불가**로 처리하기 위한 데이터다(4.3절).
 * 체인저는 1분에 10개를 처리해야 하므로 시행착오할 여유가 없다.
 *
 * 반환 규약: 게임 규칙 위반은 `{ ok: false, reason }`, 호출자의 계약 위반(중복 id 등)은 throw.
 * 자세한 구분 근거는 `room.ts` 상단 주석 참조.
 */

import {
  ROT_STEP_COUNT,
  type Cell3,
  type CatalogEntry,
  type InstanceId,
  type ObjectInstance,
  type PaletteIndex,
  type PlayerId,
  type RoomBounds,
  type RoomConfig,
  type RoomState,
  type Rot,
  type RotSymmetry,
  type Verb,
} from './types'
import { rectContains, type CellRect } from './floorPlanModel'
import { isInBounds, cellKey, occupiedCells } from './grid'
import {
  addObject,
  buildOccupancy,
  countByPlayer,
  removeObject,
  updateObject,
  type Catalog,
} from './room'

export type VerbRejection =
  /**
   * 방 경계 밖. 체인저 문맥(`shouldKeepHeightOnMove`)에서는 높이를 바꾸는 옮김도 이 사유다 - 그 물건이 갈 수 있는 경계가 자기 높이라서(`applyMove`).
   * 같은 이유로 여럿이 판의 **남의 구획**도 이 사유다(`allowedRect`) - 그 사람이 손댈 수 있는 경계가 자기 구획이다.
   * 새 사유를 만들지 않은 이유는 P1의 `off-surface`와 같다: 사유 집합은 `editor.reject.*` 문구와 1:1이고, 정상 도구는
   * 남의 구획 물건을 애초에 고를 수 없어(잠금) 이 자리에 닿지 않는다 - 닿는 것은 와이어로 온 값뿐이다(11.3절).
   */
  | 'out-of-bounds'
  | 'cell-occupied'
  | 'placement-limit'
  | 'unknown-catalog'
  | 'unknown-instance'
  | 'verb-not-allowed'
  | 'no-visible-change'
  | 'already-changed'

export type VerbResult = { ok: true; state: RoomState } | { ok: false; reason: VerbRejection }

/**
 * 동사 요청 한 건 - 아래 `apply*` 함수들의 입력을 **그대로** 데이터로 옮긴 모양이다(로드맵 M4).
 *
 * 매치 리듀서(`game/match/matchChange.ts`)와 사람 체인저의 도구(`input/changerTool.ts`)가 같은 요청을 만들고 같은
 * 함수(`applyVerbRequest`)로 집행한다. 원래 `game/match/matchTypes.ts`에 살았는데 도구가 필요로 하게 되어 여기로 내렸다 -
 * `input/`은 `game/`을 보지 않으므로(CLAUDE.md 레이어 방향) 요청의 모양은 그 둘이 함께 볼 수 있는 층에 있어야 한다.
 * `add`의 `placedBy`는 호스트가 신원으로 찍으므로 요청에는 없다.
 */
export type VerbRequest =
  | Readonly<{ verb: 'add'; obj: Omit<ObjectInstance, 'placedBy'> }>
  | Readonly<{ verb: 'remove'; id: InstanceId }>
  | Readonly<{ verb: 'move'; id: InstanceId; cell: Cell3 }>
  | Readonly<{ verb: 'rotate'; id: InstanceId; rot: Rot }>
  | Readonly<{ verb: 'recolor'; id: InstanceId; colorId: PaletteIndex }>

/** 상태를 바꾸지 않는 배치 가능성 검사 결과. */
export type PlacementCheck = { ok: true } | { ok: false; reason: VerbRejection }

/**
 * 동사 검증에 필요한 주변 정보.
 * 방 단위로 한 번 만들어 재사용하는 것을 전제로 한다.
 */
export type VerbContext = Readonly<{
  catalog: Catalog
  config: RoomConfig
  bounds: RoomBounds
  /**
   * 미리 계산한 점유 맵(성능용). 생략하면 매 호출마다 `state`에서 다시 만든다.
   * 넘길 경우 반드시 `state`와 동기화된 것이어야 한다 - 낡은 맵은 검증을 거짓으로 만든다.
   */
  occupancy?: ReadonlyMap<string, InstanceId>
  /**
   * 5.1절("한 물건은 한 번만 건드릴 수 있다") 검사 스위치.
   * 1주차 꾸미기 에디터에는 체인저 개념이 없으므로 기본값 false.
   * 2주차 체인저 페이즈에서 true로 켠다. 기본값을 false로 둔 이유는
   * `changedIds`를 넘기는 것을 깜빡했을 때 "검사가 조용히 꺼진 상태"를 만들지 않기 위해서다 -
   * 켜는 쪽이 항상 명시적이다.
   */
  shouldEnforceOneChangePerObject?: boolean
  /** 체인저가 이미 건드린 오브젝트 집합. 위 스위치가 true일 때만 참조한다. */
  changedIds?: ReadonlySet<InstanceId>
  /**
   * 옮김이 높이(`cell.y`)를 바꾸지 못하게 하는 스위치 - 물건은 자기 높이에서만 옮겨진다(벽면 유지는 아니다 - 머리말).
   *
   * 생성기(`game/changeGen.tryMove`)는 처음부터 `y: obj.cell.y`로만 후보를 만들어 벽의 거울을 바닥에 눕히는 변화를 낸 적이 없다.
   * 사람 체인저의 도구는 자리를 표면 스냅 탭으로 받으므로 바닥을 누르면 y=0이 되어 그 길이 열려 있었다(M4 플레이테스트) -
   * 카탈로그의 부착면 등급(`sizeClass`)은 코어 규격 밖이라 여기서 읽을 수 없고, 생성기가 실제로 지키는 규칙은 등급과 무관하게
   * "y를 유지한다"라 그 규칙을 그대로 올렸다. 벽 물건뿐 아니라 상판 소품이 바닥으로 내려오는 옮김도 함께 막힌다 - 생성기도
   * 만들지 않는 변화다. 기본값 false - 꾸미기 에디터는 머그를 책상 위로 올리는 자유 배치가 본업이다.
   */
  shouldKeepHeightOnMove?: boolean
  /**
   * 손댈 수 있는 사각형(셀). 여럿이 판에서 **각자 자기 구획만** 고친다(로드맵 M11 규칙 2).
   *
   * 켜져 있으면 두 자리에서 답이 나온다 - 대상 물건의 자리(`resolveTarget`: 남의 구획 물건은 고를 수 없다)와
   * 놓일 자리(`canPlace`: 점유 칸이 하나라도 이 사각형 밖이면 안 된다). 사유는 `out-of-bounds`다(그 타입 주석).
   * 벽 한 칸이 구획을 가르므로 사각형은 서로 겹치지 않고, 그래서 "누구 구획인가"는 물건의 기준 칸 하나로 갈린다 -
   * `changeGen`의 `zoneId`·`matchChange.zoneOf`가 쓰는 `roomAt`과 같은 기준이다.
   *
   * 생략하면 검사가 없다 - 싱글의 생성기와 꾸미기 에디터는 방 전체가 무대다.
   */
  allowedRect?: CellRect
  /**
   * 꾸미기 페이즈의 인당 배치 수 제한(`config.placementsPerPlayer`) 검사 스위치.
   * 기본값 true - 1주차의 실제 사용처가 꾸미기 에디터다.
   * 체인저의 '추가'는 꾸미기 배치가 아니므로 그 경로에서는 false로 끈다.
   */
  shouldEnforcePlacementLimit?: boolean
}>

/**
 * 배치가 가능한지 검사한다. 상태를 바꾸지 않으므로 UI 프리뷰(고스트 표시)에서도 그대로 쓴다.
 *
 * @param ignoredId 이동·회전처럼 **자기 자신이 이미 점유 중인** 셀을 비켜줘야 하는 경우의 예외 대상.
 * @param allowedRect 손댈 수 있는 사각형(`VerbContext.allowedRect`). 점유 칸이 하나라도 밖이면 `out-of-bounds`다 -
 *                    기준 칸만 보면 물건의 꼬리가 남의 구획으로 삐져 나가고, 그 꼬리를 남이 쏘면 판정이 두 구획에 걸린다.
 */
export function canPlace(
  state: RoomState,
  catalog: Catalog,
  entry: CatalogEntry,
  cell: Cell3,
  rot: Rot,
  bounds: RoomBounds,
  occupancy?: ReadonlyMap<string, InstanceId>,
  ignoredId?: InstanceId,
  allowedRect?: CellRect,
): PlacementCheck {
  if (!catalog.has(entry.id)) return { ok: false, reason: 'unknown-catalog' }
  if (!isInBounds(cell, entry, rot, bounds)) return { ok: false, reason: 'out-of-bounds' }

  const map = occupancy ?? buildOccupancy(state, catalog)
  for (const occupied of occupiedCells(cell, entry, rot)) {
    if (allowedRect !== undefined && !rectContains(allowedRect, occupied.x, occupied.z)) {
      return { ok: false, reason: 'out-of-bounds' }
    }
    const holder = map.get(cellKey(occupied))
    if (holder !== undefined && holder !== ignoredId) {
      return { ok: false, reason: 'cell-occupied' }
    }
  }

  return { ok: true }
}

/**
 * 회전이 화면에 보이는가. 4.2절의 무변화 회전 차단 규칙 그 자체다.
 *
 * 대칭 차수가 s면 화면상 서로 다른 회전 상태는 `ROT_STEP_COUNT / s`개 주기로 반복된다.
 *  s=1 → 주기 4 (같은 각도만 무변화) / s=2 → 주기 2 (180° 무변화) / s=4 → 주기 1 (전부 무변화)
 */
export function isRotationVisible(from: Rot, to: Rot, symmetry: RotSymmetry): boolean {
  const period = ROT_STEP_COUNT / symmetry
  const delta = (to - from + ROT_STEP_COUNT) % ROT_STEP_COUNT
  return delta % period !== 0
}

/** 동사가 이번 라운드 설정에서 허용됐는가(5.3절). */
function isVerbAllowed(verb: Verb, ctx: VerbContext): boolean {
  return ctx.config.allowedVerbs.has(verb)
}

/** 5.1절: 이미 건드린 오브젝트인가. 스위치가 꺼져 있으면 항상 false. */
function isAlreadyChanged(id: InstanceId, ctx: VerbContext): boolean {
  if (ctx.shouldEnforceOneChangePerObject !== true) return false
  return ctx.changedIds?.has(id) === true
}

type TargetLookup = { ok: true; obj: ObjectInstance } | { ok: false; reason: VerbRejection }

/**
 * 손댈 수 있는 사각형 안의 물건인가(`allowedRect`). 사각형이 없으면 항상 참 - 싱글과 꾸미기는 방 전체가 무대다.
 * **기준 칸 하나로 판단한다** - 물건이 어느 구획의 것인가를 정하는 기준이 `roomAt(plan, cell.x, cell.z)` 하나여야
 * 생성기의 `zoneId`·리듀서의 주인·여기의 게이트가 같은 답을 낸다.
 *
 * 내보내는 이유: 도구(`input/changerTool`)가 **고르기 전에** 남의 구획 물건을 걸러야 하는데(M11 - 잠긴 팔레트가 아니라
 * 애초에 선택되지 않는다), 그 판단을 도구가 다시 적으면 이 게이트의 사본이 생긴다. 동사를 하나 골라 찔러 보는 길은
 * 없다 - 방장이 그 동사를 껐으면 `verb-not-allowed`가 먼저 나와 구획 판정에 닿지 못한다.
 */
export function isInAllowedRect(obj: ObjectInstance, ctx: VerbContext): boolean {
  if (ctx.allowedRect === undefined) return true
  return rectContains(ctx.allowedRect, obj.cell.x, obj.cell.z)
}

/**
 * 기존 오브젝트를 대상으로 하는 동사의 공통 전처리.
 * 거부 순서를 한 곳에 모아 동사별로 어긋나지 않게 한다: 동사 허용 → 인스턴스 존재 → 내 구획 → 이미 변경됨.
 *
 * 구획 검사가 "이미 변경됨"보다 앞인 이유: 남의 구획 물건은 애초에 내 관심사가 아니므로 그 물건을 남이 건드렸는지를
 * 이유로 내미는 것은 남의 진행을 흘리는 것이다(로드맵 M11 규칙 3 - 무엇을 했는지는 모른다).
 */
function resolveTarget(
  state: RoomState,
  id: InstanceId,
  verb: Verb,
  ctx: VerbContext,
): TargetLookup {
  if (!isVerbAllowed(verb, ctx)) return { ok: false, reason: 'verb-not-allowed' }

  const obj = state.objects.get(id)
  if (obj === undefined) return { ok: false, reason: 'unknown-instance' }
  if (!isInAllowedRect(obj, ctx)) return { ok: false, reason: 'out-of-bounds' }
  if (isAlreadyChanged(id, ctx)) return { ok: false, reason: 'already-changed' }

  return { ok: true, obj }
}

/**
 * 오브젝트 추가(꾸미기 배치 겸 체인저의 '추가' 동사).
 * 중복 id는 규칙 거부가 아니라 계약 위반이므로 `addObject`가 throw 한다.
 *
 * 5.1절 검사는 추가에도 있다 - 체인저가 없앤 물건의 id로 다시 놓으면 한 대상에 변화가 둘(`remove`·`add`)이 되고, 수색에서
 * 그 물건을 쏘는 순간 판정기(`hunt.findChangeByTarget`)가 던진다. 정상 클라이언트는 그런 id를 만들지 않지만(도구의
 * `nextAddId`가 원본·초안 id를 피한다) 와이어로 올 수 있는 값이라 집행자가 막는다(11.3절). 순서는 `resolveTarget`과
 * 같다 - 동사 허용 → (인스턴스 존재 대신) 카탈로그 존재 → 이미 변경됨.
 */
export function applyAdd(state: RoomState, obj: ObjectInstance, ctx: VerbContext): VerbResult {
  if (!isVerbAllowed('add', ctx)) return { ok: false, reason: 'verb-not-allowed' }

  const entry = ctx.catalog.get(obj.catalogId)
  if (entry === undefined) return { ok: false, reason: 'unknown-catalog' }
  if (isAlreadyChanged(obj.id, ctx)) return { ok: false, reason: 'already-changed' }

  if (ctx.shouldEnforcePlacementLimit !== false) {
    if (countByPlayer(state, obj.placedBy) >= ctx.config.placementsPerPlayer) {
      return { ok: false, reason: 'placement-limit' }
    }
  }

  const placement = canPlace(
    state,
    ctx.catalog,
    entry,
    obj.cell,
    obj.rot,
    ctx.bounds,
    ctx.occupancy,
    undefined,
    ctx.allowedRect,
  )
  if (!placement.ok) return placement

  return { ok: true, state: addObject(state, obj) }
}

/** 오브젝트 제거. 4.3절 표대로 제거는 항상 식별 가능하므로 무변화 검사가 없다. */
export function applyRemove(state: RoomState, id: InstanceId, ctx: VerbContext): VerbResult {
  const target = resolveTarget(state, id, 'remove', ctx)
  if (!target.ok) return target

  return { ok: true, state: removeObject(state, id) }
}

/**
 * 이동. 같은 셀로의 이동은 거부한다(4.3절). 미세 이동 문제는 4.1절의 그리드 해상도가 이미 막는다.
 *
 * 높이를 바꾸는 이동은 `shouldKeepHeightOnMove`가 켜져 있으면 `out-of-bounds`다 - 그 물건이 갈 수 있는 경계가 자기 높이다.
 * 벽면(x 또는 z 고정)은 검사하지 않는다 - 부착 축은 카탈로그(`sizeClass`)의 것이라 코어가 모른다(미해결, 머리말).
 * 사유를 새로 만들지 않은 이유: 거부 사유 집합은 `editor.reject.*` 문구와 1:1이고, 정상 도구(`input/changerTool`)는 요청의 y를
 * 물건의 y로 두어 이 사유에 닿지 않는다. 와이어로 온 값(11.3절)만 여기서 멈춘다.
 */
export function applyMove(
  state: RoomState,
  id: InstanceId,
  cell: Cell3,
  ctx: VerbContext,
): VerbResult {
  const target = resolveTarget(state, id, 'move', ctx)
  if (!target.ok) return target

  const { obj } = target
  const entry = ctx.catalog.get(obj.catalogId)
  if (entry === undefined) return { ok: false, reason: 'unknown-catalog' }

  if (isSameCell(obj.cell, cell)) return { ok: false, reason: 'no-visible-change' }
  if (ctx.shouldKeepHeightOnMove === true && cell.y !== obj.cell.y) return { ok: false, reason: 'out-of-bounds' }

  const placement = canPlace(state, ctx.catalog, entry, cell, obj.rot, ctx.bounds, ctx.occupancy, id, ctx.allowedRect)
  if (!placement.ok) return placement

  return { ok: true, state: updateObject(state, id, { cell }) }
}

/**
 * 회전. 이 함수가 4.2절의 "필수 차단"을 집행한다.
 * 무변화 회전은 서버 diff에는 변화 1개로 잡히지만 술래에게는 존재하지 않는 변화다.
 *
 * 회전은 점유 셀 모양을 바꾸므로(직사각형 소품의 x/z 교환) 경계·점유 재검사까지 해야 한다.
 */
export function applyRotate(
  state: RoomState,
  id: InstanceId,
  rot: Rot,
  ctx: VerbContext,
): VerbResult {
  const target = resolveTarget(state, id, 'rotate', ctx)
  if (!target.ok) return target

  const { obj } = target
  const entry = ctx.catalog.get(obj.catalogId)
  if (entry === undefined) return { ok: false, reason: 'unknown-catalog' }

  if (!isRotationVisible(obj.rot, rot, entry.rotSymmetry)) {
    return { ok: false, reason: 'no-visible-change' }
  }

  const placement = canPlace(state, ctx.catalog, entry, obj.cell, rot, ctx.bounds, ctx.occupancy, id, ctx.allowedRect)
  if (!placement.ok) return placement

  return { ok: true, state: updateObject(state, id, { rot }) }
}

/** 색 변경. 같은 팔레트 인덱스로의 변경은 화면상 무변화이므로 거부한다(4.3절). */
export function applyRecolor(
  state: RoomState,
  id: InstanceId,
  colorId: PaletteIndex,
  ctx: VerbContext,
): VerbResult {
  const target = resolveTarget(state, id, 'recolor', ctx)
  if (!target.ok) return target

  if (target.obj.colorId === colorId) return { ok: false, reason: 'no-visible-change' }

  return { ok: true, state: updateObject(state, id, { colorId }) }
}

/**
 * 요청 하나를 그 동사의 집행자에게 넘긴다. 분기는 이 한 곳이다 - 리듀서와 도구가 각자 `switch`를 들면
 * 새 동사가 붙는 날 한쪽만 갱신된다. `placedBy`는 `add`에만 쓰이고 나머지 동사는 무시한다.
 */
export function applyVerbRequest(
  state: RoomState,
  request: VerbRequest,
  placedBy: PlayerId,
  ctx: VerbContext,
): VerbResult {
  switch (request.verb) {
    case 'add':
      return applyAdd(state, { ...request.obj, placedBy }, ctx)
    case 'remove':
      return applyRemove(state, request.id, ctx)
    case 'move':
      return applyMove(state, request.id, request.cell, ctx)
    case 'rotate':
      return applyRotate(state, request.id, request.rot, ctx)
    case 'recolor':
      return applyRecolor(state, request.id, request.colorId, ctx)
  }
}

/**
 * 한 물건을 `original`의 모습으로 되돌린다(사람 체인저의 되돌리기). 초안에서 지우고, 원본에 있던 물건이면 원본 그대로를
 * 다시 놓는다 - 옮김·회전·색은 물론 제거도 같은 길이라(지운 뒤 다시 놓는다) 경로가 하나다. 원본에 없던 물건(`add`)은
 * 지우면 끝이다.
 *
 * 되돌아갈 자리를 `canPlace`로 다시 검사한다 - 옮긴 물건의 원래 자리에 그새 다른 것을 놓았을 수 있고, 초안이 한 셀에
 * 둘을 겹쳐 두는 상태는 어떤 경로로도 만들지 않는다. 막히면 그 사유(`cell-occupied`)가 그대로 거부다.
 *
 * 호스트 리듀서(`game/match/matchChange.onUndoChange`)와 체인저 도구(`input/changerTool`)가 **같은 함수**를 부른다 -
 * `applyVerbRequest`와 같은 결정이다. 한쪽이 검사 순서를 바꾸는 날 낙관적 초안과 호스트 판정이 갈리므로 여기 한 벌만 둔다.
 * 원본의 카탈로그가 없는 것은 `canPlace`와 같은 사유(`unknown-catalog`)다 - 던지지 않는다. 와이어에서 온 값으로 호스트가
 * 던지면 안 되고, 도구와 리듀서가 같은 답을 내려면 둘 다 값으로 받아야 한다.
 */
export function restoreOriginal(
  draft: RoomState,
  original: RoomState,
  id: InstanceId,
  catalog: Catalog,
  bounds: RoomBounds,
): VerbResult {
  let next = draft.objects.has(id) ? removeObject(draft, id) : draft
  const before = original.objects.get(id)
  if (before !== undefined) {
    const entry = catalog.get(before.catalogId)
    if (entry === undefined) return { ok: false, reason: 'unknown-catalog' }
    const placement = canPlace(next, catalog, entry, before.cell, before.rot, bounds)
    if (!placement.ok) return placement
    next = addObject(next, before)
  }
  return { ok: true, state: next }
}

/**
 * 사람 체인저의 검증 문맥. 한 물건 한 번(5.1절)과 옮김의 높이 유지(생성기와 같은 규칙)는 켜고, 인당 배치 수 제한은 끈다
 * (체인저의 '추가'는 꾸미기 배치가 아니다).
 * 점유 맵은 넘기지 않는다 - 초안이 요청마다 바뀌어 낡은 맵이 검증을 거짓으로 만들고, 라운드에 열두 번 부르는 함수라
 * 매번 다시 만들어도 값싸다.
 *
 * 호스트 리듀서(`matchChange`)와 도구(`changerTool`)가 같은 스위치를 여기서 받는다 - 두 자리에 각자 적혀 있으면 스위치
 * 하나가 뒤집히는 날 도구의 잠금과 호스트 판정이 갈린다.
 *
 * @param allowedRect 여럿이 판에서 이 사람의 구획(로드맵 M11). 싱글 생성기는 넘기지 않는다 - 방 전체가 무대다.
 */
export function changerVerbContext(
  catalog: Catalog,
  config: RoomConfig,
  bounds: RoomBounds,
  changedIds: ReadonlySet<InstanceId>,
  allowedRect?: CellRect,
): VerbContext {
  return {
    catalog,
    config,
    bounds,
    shouldEnforceOneChangePerObject: true,
    changedIds,
    shouldKeepHeightOnMove: true,
    shouldEnforcePlacementLimit: false,
    // 선택 필드에 `undefined`를 명시적으로 싣지 않는다(`exactOptionalPropertyTypes`) - 없는 것과 "없음을 넣은 것"은 다른 타입이다.
    ...(allowedRect === undefined ? {} : { allowedRect }),
  }
}

function isSameCell(a: Cell3, b: Cell3): boolean {
  return a.x === b.x && a.y === b.y && a.z === b.z
}
